import { constants, JobType } from "@common/_constants";
import { Defaults } from "@common/_constants/";
import { ErrorService, UserService } from "@common/_services";
import { refetch } from "@common/_services/refetch";
import { Button } from "@common/components/Atoms/Button";
import ApplyError from "@common/components/Atoms/Error";
import { FormHandler, TextArea } from "@common/components/Atoms/FormInputs";
import FileDrop from "@common/components/Molecules/FileDrop";
import Modal from "@common/components/Molecules/Modal";
import { FormField } from "@common/models";
import React from "react";

interface AlreadyAppliedProps {
    job: {
        job_name: string;
    };
    onViewJobs: () => void;
    onGoToApplication: () => void;
}

export const AlreadyApplied: React.SFC<AlreadyAppliedProps> = (
    props: AlreadyAppliedProps
) => (
    <div className="c-block">
        <h1>we have previously received your application</h1>
        <h2>{props.job.job_name}</h2>
        <div className="c-block">Next steps</div>
        <div className="c-block">
            You can view the stage of your application or view all positions in
            our network below
        </div>
        <div className="c-block">
            You can{" "}
            <a
                className="c-link"
                target="_blank"
                href={`mailto:support@relode.com?subject=Position Inquiry for ${props.job.job_name}`}
            >
                contact
            </a>{" "}
            customer support about the position if you have any questions.
        </div>
        <div className="c-modal__footer">
            <div className="c-modal__footer-left">
                <Button
                    id="view-all-jobs-button"
                    type="submit"
                    green={true}
                    onClick={props.onViewJobs}
                >
                    view all jobs
                </Button>
            </div>
            <div className="c-modal__footer-right">
                <Button
                    id="view-app-status-button"
                    type="button"
                    onClick={props.onGoToApplication}
                >
                    view application stage
                </Button>
            </div>
        </div>
    </div>
);

interface ApplyThankYouProps {
    isOpen: boolean;
    closeModal: () => void;
    onViewJobs: () => void;
    job: {
        job_name: string;
    };
    onGoToApplication: (id?: number) => void;
}
export const ApplyThankYou: React.SFC<ApplyThankYouProps> = (
    props: ApplyThankYouProps
) => {
    if (!props.isOpen) return null;

    return (
        <Modal
            size="xs"
            title="thanks! we'll review and be in touch soon"
            isOpen={props.isOpen}
            closeModal={props.closeModal}
        >
            <br />
            <div className="c-block">
                <p>
                    Thank you for submitting your application to the{" "}
                    {props.job.job_name} position. Here's what happens next:
                </p>
                <ul>
                    <li>
                        One of our recruiters will review your qualifications.
                    </li>
                    <li>
                        We'll connect with you to go over your job history and
                        share more information about the position to make sure
                        it's a good fit.
                    </li>
                    <li>
                        We'll also keep you informed during every step of the
                        hiring process.
                    </li>
                </ul>
                <p>
                    Log in to your Relode account at any time to check your
                    stage!
                </p>
                <p>
                    Talk soon,
                    <br />
                    Team Relode
                </p>
            </div>
            <div className="c-modal__footer">
                <div className="c-modal__footer-left">
                    <Button
                        id="view-all-jobs-button"
                        type="submit"
                        green={true}
                        onClick={props.onViewJobs}
                    >
                        view all jobs
                    </Button>
                </div>
                <div className="c-modal__footer-right">
                    <Button
                        id="view-app-status-button"
                        type="button"
                        onClick={() => props.onGoToApplication()}
                    >
                        view application stage
                    </Button>
                </div>
            </div>
        </Modal>
    );
};

interface FormFieldExtension extends FormField {
    goodfit: boolean;
}

interface ApplyForJobOkayModel {
    newJobCandidateId: number;
    job_candidate_id: number;
    job_candidate_interest_token: string;
    job_id: number;
    newJobCandidateName: string;
}

interface ApplyForJobProps {
    isOpen: boolean;
    onOkay: (arg: ApplyForJobOkayModel) => void;
    onCancel: () => void;
    onViewJobs: () => void;
    onGoToApplication: (id?: number) => void;
    closeModal: () => void;
    job: {
        job_id: number;
        job_name: string;
        job_type: JobType;
        location: string;
    };
}
interface ApplyForJobState {
    existingJobCandidateId?: number;
    formFields: {
        goodfit: FormField;
    };

    doValidateFile: boolean;
    resume: {
        resumeFileId?: number;
        isValid: boolean;
        validationMessages: string[];
    };
    error: boolean;
    errorMessages: string[];
    processing: boolean;
    formIsValid: boolean;
}
export class ApplyForJob extends React.Component<
    ApplyForJobProps,
    ApplyForJobState
> {
    constructor(props) {
        super(props);

        this.emptyState = this.emptyState.bind(this);
        this.onCloseModal = this.onCloseModal.bind(this);
        this.onFormFieldChange = this.onFormFieldChange.bind(this);
        this.onSubmit = this.onSubmit.bind(this);
        this.handleResumeFileAdd = this.handleResumeFileAdd.bind(this);
        this.handleResumeFileRemove = this.handleResumeFileRemove.bind(this);
        this.handleResumeFileError = this.handleResumeFileError.bind(this);
        this.handleResumeFileUploading = this.handleResumeFileUploading.bind(
            this
        );
        this.handleResumeFileUploaded = this.handleResumeFileUploaded.bind(
            this
        );

        this.state = this.emptyState();
    }

    componentWillReceiveProps(nextProps) {
        if (nextProps.isOpen && nextProps.isOpen !== this.props.isOpen) {
            this.fetchJobCandidateIdForJob(nextProps.job.job_id);
        }
    }

    emptyState() {
        return {
            doValidateFile: true,
            error: false,
            errorMessages: [],
            existingJobCandidateId: undefined,
            processing: false,
            resume: {
                isValid: true,
                resumeFileId: undefined,
                validationMessages: [],
            },
            formIsValid: true,
            formFields: {
                goodfit: {
                    name: "goodfit",
                    type: "text",
                    value: "",
                    label: "",
                    isValid: true,
                    validationMessages: [] as string[],
                    rules: [] as Array<
                        (field: FormField, fieldSet?: any) => boolean
                    >,
                },
            },
        };
    }

    onCloseModal() {
        this.setState(
            prevState => ({
                ...this.emptyState(),
                existingJobCandidateId: prevState.existingJobCandidateId,
            }),
            this.props.closeModal
        );
    }

    onFormFieldChange(name, value) {
        const formFields = this.state.formFields;
        FormHandler.handleInputChange(name, value, formFields);
        this.setState({ formFields });
    }

    onSubmit(e) {
        e.preventDefault();

        this.setState({ doValidateFile: true }, () => {
            const formFields = this.state.formFields;
            const isValid =
                FormHandler.validate(formFields) &&
                !!this.state.resume.resumeFileId;

            if (!isValid) {
                this.setState(prevState => {
                    if (!prevState.resume.resumeFileId) {
                        const resume = {
                            ...prevState.resume,
                            isValid: false,
                            validationMessages: ["A resume upload is required"],
                        };

                        return { formFields, formIsValid: false, resume };
                    }
                    return {
                        formFields,
                        formIsValid: false,
                        resume: prevState.resume,
                    };
                });
            } else {
                this.setState(
                    { formFields, formIsValid: true, processing: true },
                    () => {
                        const saveObj = {
                            is_apply: true,
                            job_id: this.props.job.job_id,
                            candidate_statement_text: this.state.formFields
                                .goodfit.value,
                            candidate_resume_file_id: this.state.resume
                                .resumeFileId,
                        };

                        const url = `${constants.GlobalConfig.apiUrl}/pro/v2/jobcandidates`;
                        const settings = {
                            headers: {
                                "Content-Type": "application/json",
                            },
                            body: JSON.stringify(saveObj),
                            method: "POST",
                        };

                        refetch(url, settings)
                            .then(ErrorService.handleErrors)
                            .then(resp => resp.json())
                            .then(data => {
                                const {
                                    job_candidate_id,
                                    job_candidate_interest_token,
                                    job_id,
                                } = data.result;

                                const user = UserService.getUserFromStorage();

                                this.setState(this.emptyState, () =>
                                    this.props.onOkay({
                                        newJobCandidateId: job_candidate_id,
                                        job_candidate_id,
                                        job_candidate_interest_token,
                                        job_id,
                                        newJobCandidateName: !!user
                                            ? user.displayName
                                            : "",
                                    })
                                );
                            })
                            .catch(err => {
                                let messages = [err.message];
                                if (err.responseData) {
                                    messages = err.responseData.error_messages;
                                }

                                this.setState({
                                    error: true,
                                    errorMessages: messages,
                                    processing: false,
                                });
                                console.log(err.responseData);
                            });
                    }
                );
            }
        });
    }

    handleResumeFileAdd(fileId) {
        const resume = {
            ...this.state.resume,
            resumeFileId: fileId,
            isValid: true,
        };
        this.setState({ resume });
    }

    handleResumeFileRemove(fileId) {
        const resume = { ...this.state.resume, resumeFileId: undefined };
        this.setState({ resume });
    }

    handleResumeFileUploading() {
        this.setState({ processing: true });
    }

    handleResumeFileUploaded() {
        this.setState({ processing: false });
    }

    handleResumeFileError(errorMsg) {
        this.setState({ processing: false });
        const resume = {
            ...this.state.resume,
            validationMessages: [errorMsg],
            isValid: false,
        };
        this.setState({ resume });
    }

    fetchJobCandidateIdForJob(jobId) {
        const url = `${constants.GlobalConfig.apiUrl}/pro/v2/jobcandidates/id?jobId=${jobId}`;

        const settings = {
            headers: {
                "Content-Type": "application/json",
            },
            method: "GET",
        };

        refetch(url, settings)
            .then(resp => ErrorService.handleErrorsWithIgnore(resp, [200, 404]))
            .then(resp => (resp.ok ? resp.json() : { notFound: true }))
            .then(data => {
                const existingJobCandidateId = data.notFound
                    ? null
                    : data.result;
                this.setState({ existingJobCandidateId });
            })
            .catch(err => {
                let messages = [err.message];
                if (err.responseData) {
                    messages = err.responseData.error_messages;
                }

                this.setState({
                    error: true,
                    errorMessages: messages,
                });
            });
    }

    splitName(name) {
        if (!name) {
            throw new Error("Name must be a non-empty string");
        }

        const lastSpaceIndex = name.lastIndexOf(" ");
        if (lastSpaceIndex === -1) {
            return [name, ""];
        }

        return [
            name.substring(0, lastSpaceIndex),
            name.substring(lastSpaceIndex + 1, name.length),
        ];
    }

    render() {
        return (
            <Modal
                size="xs"
                title="apply for position"
                isOpen={this.props.isOpen}
                closeModal={this.onCloseModal}
                loading={this.state.processing}
            >
                {this.state.existingJobCandidateId && (
                    <AlreadyApplied
                        onViewJobs={this.props.onViewJobs}
                        onGoToApplication={() =>
                            this.props.onGoToApplication(
                                this.state.existingJobCandidateId
                            )
                        }
                        job={this.props.job}
                    />
                )}

                {!this.state.existingJobCandidateId && (
                    <form onSubmit={this.onSubmit}>
                        <div className="c-block">
                            <h1 className="c-block__title">
                                {this.props.job.job_name}
                            </h1>
                            <p className="u-text-gray">
                                <span
                                    style={{
                                        display: "inline-block",
                                        marginRight: "16px",
                                    }}
                                >
                                    {this.props.job.job_type}
                                </span>
                                <span>{this.props.job.location}</span>
                            </p>
                        </div>

                        <div className="c-block">
                            <div>
                                <div className="c-control__label">
                                    <label htmlFor="resumeDropZone">
                                        resume
                                    </label>
                                </div>
                                <div className="c-control__wrapper">
                                    <FileDrop
                                        name="resumeDropZone"
                                        isMultiple={false}
                                        handleAdd={this.handleResumeFileAdd}
                                        handleRemove={
                                            this.handleResumeFileRemove
                                        }
                                        onFileUploading={
                                            this.handleResumeFileUploading
                                        }
                                        onFileUploaded={
                                            this.handleResumeFileUploaded
                                        }
                                        onFileError={this.handleResumeFileError}
                                        isValid={this.state.resume.isValid}
                                        validationMessages={
                                            this.state.resume.validationMessages
                                        }
                                        fileCategory={
                                            Defaults.FileCategoryType
                                                .OriginalResume
                                        }
                                        acceptedFiles={
                                            ".doc,.docx,.pdf,.rtf,.txt,.rtf,.html,.htm"
                                        }
                                    />
                                </div>
                            </div>
                        </div>

                        <TextArea
                            readOnly={false}
                            autoComplete="Off"
                            required={false}
                            name="goodfit"
                            label="Why are you a good fit for this job?"
                            value={this.state.formFields.goodfit.value}
                            maxLength={1800}
                            isValid={this.state.formFields.goodfit.isValid}
                            validationMessages={
                                this.state.formFields.goodfit.validationMessages
                            }
                            onChange={this.onFormFieldChange}
                        />

                        <div className="c-block">
                            <ApplyError
                                show={
                                    !this.state.formIsValid || this.state.error
                                }
                                description={
                                    this.state.errorMessages &&
                                    this.state.errorMessages.length
                                        ? this.state.errorMessages[0]
                                        : ""
                                }
                            />
                            <Button
                                id="submit-application-button"
                                block={true}
                                green={true}
                                type="submit"
                                onClick={this.onSubmit}
                            >
                                submit application
                            </Button>
                        </div>

                        <div className="c-block u-text-legal">
                            Upon submission, you will be contacted by Relode to
                            process your application, verify your
                            qualifications, and discuss your interest in this
                            position.
                        </div>
                    </form>
                )}
            </Modal>
        );
    }
}
